/* eslint eslint-comments/no-use: off */
/* eslint-disable github/no-then */

/**
 * Creates a new Promise-like object that can be resolved any time after its definition.
 *
 * @example
 * ```ts
 *  const deferred = new Deferred<number>()
 *  // do things
 *
 *  deferred.resolve(42)
 *
 *  const value = await deferred
 *  // value === 42
 * ```
 */
export class Deferred<T> implements Promise<T> {
  #promise: Promise<T>

  declare catch: Promise<T>['catch']
  declare finally: Promise<T>['finally']
  declare then: Promise<T>['then']
  /**
   * Rejects the `Deferred` promise with a given error reason
   * @param reason The reason for the promise to be rejected
   */
  declare reject: <Err extends Error>(reason?: Err) => unknown
  /**
   * Resolves the `Deferred` promise to a given value
   * @param value A value or promise to a value that the underlying deferred promise will resolve to
   */
  declare resolve: (value: T | PromiseLike<T>) => void;

  [Symbol.toStringTag] = 'Deferred'

  constructor() {
    this.#promise = new Promise<T>((resolve, reject) => {
      this.resolve = resolve
      this.reject = reject
    })

    this.then = this.#promise.then.bind(this.#promise)
    this.catch = this.#promise.catch.bind(this.#promise)
    this.finally = this.#promise.finally.bind(this.#promise)
  }
}
